// src/pages/Auth/Login.jsx - WITH FIREBASE AUTH & PASSWORD RESET & HELP SUPPORT
import React, { useState } from 'react';
import { ChefHat, Loader, Mail, AlertCircle, CheckCircle, HelpCircle, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import KitchenStaffService from '../../services/KitchenStaffService';
import AuthService from '../../services/AuthService';
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, sendPasswordReset, resendVerification } = useApp();

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setIsLoading(true);
  setNeedsVerification(false);

  try {
    // ✅ STEP 1: Try Firebase Auth first (works for everyone)
    const authResult = await AuthService.signIn(email, password);
    
    if (!authResult.success) {
      setError(authResult.message);
      if (authResult.needsVerification) {
        setNeedsVerification(true);
      }
      setIsLoading(false);
      return;
    }

    console.log('✅ Firebase Auth successful');

    // ✅ STEP 2: Check if kitchen staff FIRST (before loading full data)
    const deviceId = KitchenStaffService.generateDeviceId();
    const kitchenResult = await KitchenStaffService.verifyKitchenLogin(email, deviceId);
    
    if (kitchenResult.success) {
      console.log('✅ Kitchen staff login verified');
      
      // Store kitchen staff info
      localStorage.setItem('kitchenStaffId', kitchenResult.staffId);
      localStorage.setItem('kitchenDeviceId', deviceId);
      localStorage.setItem('kitchenRestaurantId', kitchenResult.restaurantId);
      
      if (typeof onLogin === 'function') {
        onLogin('kitchen', kitchenResult.restaurantId);
      }
      return;
    }

    // ✅ STEP 3: If not kitchen staff, try normal login
    const result = await login(email, password);
    
    if (result && result.success) {
      if (typeof onLogin === 'function') {
        onLogin(result.type, result.restaurantId);
      }
      return;
    }

    // If all fail
    setError(result?.message || 'Invalid credentials');
    setIsLoading(false);

  } catch (error) {
    console.error('❌ LOGIN: Exception', error);
    setError('An error occurred. Please try again.');
    setIsLoading(false);
  }
};

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetLoading(true);

    try {
      const result = await sendPasswordReset(resetEmail);
      
      if (result.success) {
        setResetMessage({ type: 'success', text: result.message });
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setResetMessage({ type: 'error', text: 'Failed to send reset email' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      const result = await resendVerification();
      if (result.success) {
        setError('Verification email sent! Please check your inbox.');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to send verification email');
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <Mail className="w-16 h-16 mx-auto text-orange-500 mb-4" />
            <h1 className="text-3xl font-bold text-gray-800">Reset Password</h1>
            <p className="text-gray-600 mt-2">Enter your email to receive reset link</p>
          </div>

          <form onSubmit={handleForgotPassword} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={resetLoading}
              />
            </div>

            {resetMessage && (
              <div className={`p-4 rounded-lg border flex items-start gap-3 ${
                resetMessage.type === 'success' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {resetMessage.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <p className={`text-sm ${
                  resetMessage.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {resetMessage.text}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={resetLoading}
              className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resetLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmail('');
                setResetMessage('');
              }}
              className="w-full text-gray-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Back to Login
            </button>
          </form>

          {/* Help Support at Bottom */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600 mb-2">Need help?</p>
            <a
              href="mailto:contact@zyppro.com"
              className="text-orange-500 hover:text-orange-600 font-medium text-sm flex items-center justify-center gap-1"
            >
              <Mail className="w-4 h-4" />
              contact@zyppro.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <ChefHat className="w-16 h-16 mx-auto text-orange-500 mb-4" />
          <h1 className="text-3xl font-bold text-gray-800">QR Restaurant</h1>
          <p className="text-gray-600 mt-2">Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter your email"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
         <div className="relative">
  <input
    type={showPassword ? 'text' : 'password'}
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-10"
    placeholder="Enter password"
    required
    disabled={isLoading}
  />
  <button
    type="button"
    onClick={() => setShowPassword(!showPassword)}
    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
    disabled={isLoading}
  >
    {showPassword ? (
      <EyeOff className="w-5 h-5" />
    ) : (
      <Eye className="w-5 h-5" />
    )}
  </button>
</div>

          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
                Remember me
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-orange-500 hover:text-orange-600 font-medium"
            >
              Forgot Password?
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-600 text-sm">{error}</p>
                {needsVerification && (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="text-orange-500 hover:text-orange-600 font-medium text-sm mt-2"
                    disabled={isLoading}
                  >
                    Resend Verification Email
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        {/* Need Help Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-1">Need Help?</h3>
                <p className="text-xs text-blue-700 mb-2">
                  Having trouble logging in? Contact our support team
                </p>
                <a
                  href="mailto:contact@zyppro.com?subject=Login%20Issue%20-%20QR%20Restaurant&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20am%20facing%20login%20issues%20with%20my%20account.%0D%0A%0D%0AEmail:%20%0D%0AIssue:%20%0D%0A%0D%0APlease%20help.%0D%0A%0D%0AThank%20you"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition"
                >
                  <Mail className="w-4 h-4" />
                  contact@zyppro.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Zyppro v1.0 (Beta)
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;